// a library to wrap and simplify api calls
import type { APP_USER_ACTIVITY_TYPE } from '@hedepy/common/enums/APP_USER_ACTIVITY_TYPE'
import type { Event } from '@hedepy/common/schemas/modules/event/event'
import type { CallDiagnosticsPostRequest } from '@hedepy/common/schemas/routes/api/v2/callDiagnostics'
import type {
  CartCheckoutPostRequestSchema,
  CartCheckoutPostResponseSchema,
  CartResolvePostRequestSchema,
  CartResolvePostResponseSchema,
  FreeIntroSessionEligibilityResponse,
  TranscriptAgentTogglePostBody,
} from '@hedepy/common/schemas/routes/api/v2/event'
import type { SchedulePostRequest, SchedulePutRequest } from '@hedepy/common/schemas/routes/api/v2/schedule'
import type { ServiceListQuery } from '@hedepy/common/schemas/routes/api/v2/service'
import type {
  TherapistListRequest,
  TherapistListResponse,
  TherapistOfficeRequest,
} from '@hedepy/common/schemas/routes/api/v2/therapist'
import type { UserSubscriptionChangeBody } from '@hedepy/common/schemas/routes/api/v2/userSubscription'
import axios, { type AxiosInstance } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { type z } from 'zod'

import { type AUTOMATIC_MESSAGE_TYPES } from '@/legacy-src/components/therapistAccount/types'
import { type CompanyEmployeeToken } from '@/legacy-src/types/UserStateType'

import { type CounsellingSessionType } from '../components/counselling/types'
import { type SelectedTherapistType, type SurveyAnswerType, type SurveyFinishedType } from '../components/survey/types'
import { type CompanyContact, type VibeSettingsType } from '../components/vibe/types'
import { REFRESH_TOKEN_KEY } from '../constants/storage'
import { HIDE_UI_PARAM_NAME } from '../hooks/useIsAppWebView'
import { type FeedbackReviewData } from '../types/ClientStateType'
import { API_URL } from '../utilities/apiUtils'
import handleLogout from '../utilities/handleLogout'

type CartResolvePostRequestSchema = z.infer<typeof CartResolvePostRequestSchema>
type CartResolvePostResponseSchema = z.infer<typeof CartResolvePostResponseSchema>
type CartCheckoutPostRequestSchema = z.infer<typeof CartCheckoutPostRequestSchema>
type CartCheckoutPostResponseSchema = z.infer<typeof CartCheckoutPostResponseSchema>

const notServer = typeof window !== 'undefined'

const modifyOrigin = (origin?: string) => {
  if (!window) return origin

  const urlParams = new URLSearchParams(window.location.search)
  const isApp = urlParams.get(HIDE_UI_PARAM_NAME)

  if (isApp) {
    return `app-${origin || 'regular'}`
  } else {
    return origin
  }
}

export const createApi = () =>
  axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': 'cs', // TODO: Backend locale i18n
      Accept: 'application/json',
    },
    timeout: 20_000,
  })

export const axiosApi = createApi()

const endpoints = (api: AxiosInstance & { getState?: any; dispatch?: any }) => {
  const connect = (getState: any, dispatch: any) => {
    api.getState = getState
    api.dispatch = dispatch
  }

  const setHeader = (name: string, value: string) => {
    if (value) {
      api.defaults.headers.common[name] = value
    } else {
      delete api.defaults.headers.common[name]
    }
  }

  const handleLogoutWithHeaderReset = () => {
    setHeader('Authorization', '')
    handleLogout()
  }

  const refresh =
    (refreshApi: (token: string) => Promise<any>) =>
    (refreshToken, actions: object[] = []) => {
      if (refreshToken) {
        return refreshApi(refreshToken)
          .then(({ data, status }) => {
            if (status === 200) {
              setHeader('Authorization', `Bearer ${data.accessToken}`)
              actions.forEach((action: object) => {
                api.dispatch(action)
              })
            } else {
              handleLogoutWithHeaderReset()
            }
          })
          .catch(() => {
            handleLogoutWithHeaderReset()
          })
      }
    }

  const apiEndpoints = {
    // get
    getUserData: () => api.get('app-user'),
    getAllClients: () => api.get('therapist/clients'),
    getAllClientsSegmented: () => api.get('therapist/clients-segmented'),
    getAllServices: (params?: ServiceListQuery) => api.get('service', { params }),
    getAllServicesPrivate: (params?: ServiceListQuery) => api.get('service/authenticated', { params }),
    getAllEvents: () => api.get('event'),
    getCurrentEvents: () => api.get('event/current'),
    getPastEvents: () => api.get('event/past'),
    getClientEvents: (clientId) => api.get(`event/client/${clientId}`),
    getTherapistOfficeDistricts: (serviceId: number) => api.get('therapist/office-districts?serviceId=' + serviceId),
    getOpenTherapists: () => api.get('therapist/list-open'),
    postTherapistList: (data: TherapistListRequest) => api.post<TherapistListResponse>('therapist/list', data),
    getSchedule: () => api.get('schedule'),
    getFreeSlots: (therapistId: number, dateTo: number, serviceId: number) =>
      api.get(`schedule/free/${therapistId}/${dateTo}/${serviceId}`),
    getVideoToken: (eventId: number) => api.get(`event/token/${eventId}`),
    getEventAiSetup: (eventId: number) => api.get(`event/${eventId}/ai-setup`),
    getCallDiagnosticsToken: (eventId: number | null) =>
      api.get('call-diagnostics/token', {
        params: eventId === null ? {} : { eventId },
      }),
    getReviews: (therapistUserId: number) => api.get(`review/${therapistUserId}`),
    getAllMyReviews: () => api.get(`review/all`),
    getSpecializations: () => api.get('therapist/specializations'),
    getInsuranceCompanies: () => api.get('insurance?source=api-function'),
    getTrainings: () => api.get('therapist/trainings'),
    getTherapistCategories: () => api.get('therapist/categories'),
    getTherapistData: <T = any>(therapistUserId: number) => api.get<T>(`therapist/detail/${therapistUserId}`),
    getTherapist: <T = any>(therapistId: number) => api.get<T>(`therapist/${therapistId}`),
    getDiagnosis: (serviceId?: number) =>
      serviceId ? api.get(`diagnosis?serviceId=${serviceId}`) : api.get('diagnosis'),
    getDiagnosisPreview: (data: {}) => api.post('diagnosis/preview', data),
    getSubscriptions: (initPrice?: number) => api.get(`subscriptions${initPrice ? `/${initPrice}` : ''}`),
    getMessagesToken: () => api.get('messages/token'),
    getInstantTherapists: () => api.get('instant/list'),
    getFeedback: (therapistId) => api.get(`review/client/${therapistId}`),
    getReferral: () => api.get('voucher/referral'),
    getStatistics: (lastDays: number) => api.get(`stats?lastDays=${lastDays}`),
    getFeedbackComparison: (lastDays: number) => api.get(`stats/nps?lastDays=${lastDays}`),
    getLongTimeTrend: (property: string) => api.get(`stats/long-term/${property}`),
    getDepartmentsLocations: (companyId: number) => api.get(`vibe/departments-locations/${companyId}`),
    getMoodChart: (
      companyId: number,
      departmentId: string[],
      locationId: string[],
      from: string,
      to: string,
      divisionId: string[],
    ) => {
      const urlParams = new URLSearchParams(
        Object.entries({ departmentId, locationId, divisionId }).flatMap(([key, values]) =>
          Array.isArray(values) ? values.map((value) => [key, value]) : [[key, values]],
        ),
      )
      return api.get(`vibe/mood/${companyId}?${urlParams.toString()}&from=${from}&to=${to}`)
    },
    getCounsellingSessions: () => api.get(`counselling`),
    getContactList: (companyId: number) => api.get(`company/${companyId}/contact`),
    getOneContact: (companyId: number, contactId: number) => api.get(`company/${companyId}/contact/${contactId}`),
    getQuestions: () => api.get('survey/question'),
    initSurvey: (userId: number, token: string) => api.get(`/survey/${userId}/${token}`),
    getCompany: (slug: string, activationLinkId?: string, token?: string) =>
      activationLinkId && token
        ? api.get(`company/public/${slug}?activationLinkId=${activationLinkId}&activationLinkToken=${token}`)
        : api.get(`company/public/${slug}`),
    getAllowedPremiumContent: () => api.get(`premium-content`),
    getVisiblePremiumContent: () => api.get(`premium-content/teasers`),
    getCompanyQuota: (companyId: number) => api.get(`company/${companyId}/quota`),
    getCompanySlackLink: (slug: string, uuid: string) => api.get(`slack/auth?slug=${slug}&onlyLink=true&uuid=${uuid}`),
    getCompanySlackConnection: (slug: string, companyId: number, code: string) =>
      api.get(`slack/auth/callback/${slug}/${companyId}?code=${code}`),
    getInvoiceForEvent: (eventId: number) => api.get(`/event/invoice/${eventId}`, { responseType: 'blob' }),
    getMembershipPlans: () => api.get(`/membership/plans`),
    getVibeSettings: (companyId: number) => api.get(`/vibe/settings/${companyId}`),
    getIsInNewsletter: () => api.get(`sendpulse`),
    getAutomaticMessage: () => api.get(`/therapist/automatic-message`),
    getUserActivity: (type?: APP_USER_ACTIVITY_TYPE) =>
      api.get(`/app-user/activity${type === undefined ? '' : `?type=${type}`}`),
    getAiTrial: () => api.get(`/ai-trial`),
    postAiTrial: () => api.post(`/ai-trial`),
    getUserSubscriptionCancelReasons: () => api.get('/userSubscription/cancelReasons'),
    getUserSubscriptionCancelPreview: (userSubscriptionId: number) =>
      api.get(`/userSubscription/${userSubscriptionId}/cancelPreview`),
    postUserSubscriptionCancelReason: (userSubscriptionId: number, cancelReasonOptionId: number) =>
      api.post(`/userSubscription/${userSubscriptionId}/cancelReason`, { cancelReasonOptionId }),
    getUserReviews: (page: number) => api.get(`/review?page=${page}`),
    getVocativeName: (name: string) => api.get(`natural-language/name-vocative?nominative=${name}`),
    downloadClientInvoices: (url: string) =>
      api.get(`client-invoices/download/${url}`, {
        responseType: 'blob',
        timeout: 100_000,
      }),
    getClientInfo: (clientId: number) => api.get(`/app-user/client/${clientId}`),
    getSmartBillData: () => api.get('/therapist/smartbill'),
    getCallQualityReview: (eventId: number) => api.get(`/event/${eventId}/call-quality-review`),
    getUserSubscription: () => api.get('/userSubscription'),
    getUserSubscriptionDetail: (userSubscriptionId: number) =>
      api.get(`/userSubscription/detail/${userSubscriptionId}`),
    getEventTopicIdentifiers: (eventId: number) => api.get(`/event/${eventId}/event-topic-identifiers`),

    // post
    refreshToken: (refreshToken: string) =>
      api.post(
        'oauth/refresh-token',
        {},
        {
          headers: { Authorization: `Bearer ${refreshToken}` },
        },
      ),
    transaction: (eventId: number[], paymentMethod: string) =>
      api.post('transaction', {
        eventId: eventId,
        method: paymentMethod,
      }),
    addSchedule: (scheduleData: SchedulePostRequest) => api.post('schedule', { ...scheduleData }),
    reserveEvents: ({
      events,
      emailNote = '',
      videoChat = false,
      timezone,
      initialVoucherCall,
      paymentMethod,
      origin,
      originParam,
    }: {
      events: Pick<Event, 'startDate' | 'endDate' | 'therapistUserId' | 'clientId' | 'serviceId' | 'status' | 'type'>[]
      emailNote?: string
      videoChat?: boolean
      timezone?: string
      initialVoucherCall?: boolean
      paymentMethod?: string
      origin?: string
      originParam?: number
    }) =>
      api.post('event', {
        events,
        emailNote,
        onMeeting: videoChat,
        timezone,
        initialVoucherCall,
        paymentMethod,
        origin: modifyOrigin(origin),
        originParam,
      }),
    sendFeedback: ({ therapistId, data }: { therapistId: number; data: FeedbackReviewData }) =>
      api.post(`review/client/${therapistId}`, data),
    changePassword: ({
      token,
      email,
      password,
      isActivated,
      marketingConsent,
      termsConsent,
      companyEmployee,
    }: {
      token: string
      email: string
      password: string
      isActivated?: boolean
      marketingConsent?: boolean
      termsConsent?: boolean
      companyEmployee?: CompanyEmployeeToken
    }) =>
      api.post(
        'register/change-password',
        { password, email, isActivated, marketingConsent, termsConsent, companyEmployee },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    cancelPrebookedEvents: (eventsId: number[]) => api.post('event/cancel-prebooked', eventsId),
    createSubscription: (data) => api.post('userSubscription', data),
    pauseUserSubscription: (userSubscriptionId: number) => api.post(`userSubscription/${userSubscriptionId}/pause`),
    changeSubscription: (userSubscriptionId: number, subscriptionId: number, data?: UserSubscriptionChangeBody) =>
      api.post(
        `userSubscription/change?subscriptionId=${subscriptionId}&userSubscriptionId=${userSubscriptionId}`,
        data,
      ),
    askForClientInvoices: (data) => api.post('client-invoices/fetch?isAsyncDownloadRequest=true', data),
    saveTherapist: (data: {}, config) => api.post('therapist', data, config),
    changeEmail: (email: string) => api.post('app-user/change-email', { email }),
    changeEventDatetime: (eventId: number, data: { newStartDate: string; note?: string }) =>
      api.post(`event/change-datetime/${eventId}`, data),
    userExists: (email: string) => api.post('therapist/check-email', { email }),
    elaborateFeedback: (feedbackId: string, data) => api.post(`/vibe/elaborate/${feedbackId}`, data),
    addCompanyContact: (companyId: number, data: CompanyContact) => api.post(`company/${companyId}/contact`, data),
    updateCounsellingSession: (sessionId: number, data: Partial<CounsellingSessionType>) =>
      api.put(`counselling/${sessionId}`, data),
    addInsuranceCompanyId: (data) => api.post('insurance/therapist', data),
    saveSurveyAnswer: (userId: number, token: string, data: SurveyAnswerType) =>
      api.post(`/survey/${userId}/${token}/answer`, data),
    buyMembership: (membershipPlanId: number) => api.post(`/membership/order`, { membershipPlanId }),
    trackExperiment: (experimentId: string, experimentVersion: string) =>
      api.post(`/experiment`, { experimentId, experimentVersion }),
    setAutomaticMessage: (messageType: AUTOMATIC_MESSAGE_TYPES, active: boolean, message?: string) =>
      api.post(`therapist/automatic-message/${messageType}`, {
        message,
        active,
      }),
    createConversation: ({ therapistId, clientId }: { therapistId?: number; clientId?: number }) =>
      api.post(`/messages/create-conversation`, { therapistId, clientId }),
    setUserActivity: (type: number) => api.post(`/app-user/activity`, { type }),
    createCallDiagnostics: (data: CallDiagnosticsPostRequest) => api.post('/call-diagnostics', data),
    resolveCart: (data: CartResolvePostRequestSchema) =>
      api.post<CartResolvePostResponseSchema>(`/event/cart/resolve`, data),
    checkoutCart: (data: CartCheckoutPostRequestSchema) =>
      api.post<CartCheckoutPostResponseSchema>(`/event/cart/checkout`, data),
    getOfflineExtraFee: () => api.get('/event/offline-extra-price'),
    getFreeIntroSessionEligibility: () => api.get<FreeIntroSessionEligibilityResponse>('/event/free-intro-session'),
    submitTherapistEventTopics: (eventId: number, eventTopicsIdentifierIds: number[]) =>
      api.post(`/event/${eventId}/therapist-topics`, { eventTopicsIdentifierIds }),
    toggleTranscriptAgent: (eventId: number, body: TranscriptAgentTogglePostBody) =>
      api.post(`/event/toggle-transcript-agent/${eventId}`, body),

    // put/patch
    updateTherapistOffice: (data: TherapistOfficeRequest) => api.put('/therapist/office', data),
    editSchedule: (scheduleId: number, scheduleData: Omit<SchedulePutRequest, 'id'>) =>
      api.put('schedule', { ...scheduleData, id: scheduleId }),
    updateUserData: (data: {}) => api.put('app-user', data),
    updateTherapistData: (data: {}) => api.put(`therapist`, data),
    updateFeedback: ({ reviewId, data }: { reviewId: number; data: FeedbackReviewData }) =>
      api.patch(`review/client/${reviewId}`, data),
    updateCompanyContact: (companyId: number, contactId: number, data: CompanyContact) =>
      api.put(`/company/${companyId}/contact/${contactId}`, data),
    sendSurveyData: (userId: number, token: string, data: SurveyFinishedType | SelectedTherapistType) =>
      api.put(`/survey/${userId}/${token}`, data),
    verifyEmployeeEmail: (slug: string, email: string) => {
      return api.post(`company/${slug}/employee`, {
        companyEmail: email,
      })
    },
    confirmCompany: (companyEmployee) => api.post(`company/employee/confirm`, companyEmployee),
    verifyEmployeeCode: (slug: string, email: string) => {
      return api.post(`company/${slug}/employee`, {
        code: email,
      })
    },
    updateVibeSettings: (companyId: number, settings: Partial<VibeSettingsType>) => {
      return api.patch(`vibe/settings/${companyId}`, settings)
    },
    saveDiagnosis: (data: {}) => api.put('diagnosis', data),
    connectAccount: (feedbackId: string, uuid: string) => {
      return api.post(`/vibe/connect-account/${feedbackId}`, {
        uuid,
      })
    },
    updateSmartBillData: (data: any) => api.put('/therapist/smartbill', data),
    updateCallQualityReview: (eventId: number, data: any) => api.put(`/event/${eventId}/call-quality-review`, data),

    // delete
    deleteSchedule: (scheduleId: number) => api.delete(`schedule/${scheduleId}`),
    cancelEvent: (eventId: number) => api.delete(`event/cancel/${eventId}`),
    deleteSubscription: (userSubscriptionId: number) => api.delete(`userSubscription/${userSubscriptionId}`),
    deleteInsuranceCompanyId: (insuranceId: number) => api.delete(`insurance/therapist/${insuranceId}`),
    cancelMembership: (membershipId: number) => api.delete(`membership/${membershipId}`),
    deleteFromNewsletter: () => api.delete(`sendpulse`),
    deleteConversation: (therapistUserId: number) =>
      api.delete(`messages`, { data: { therapistUserId: therapistUserId } }),

    // helpers
    setHeader: (key: string, value: string) => setHeader(key, value),
    setAuthorization: (auth: string) => {
      setHeader('Authorization', auth)
    },

    // head
    headClientInvoices: (url: string) => api.head(`client-invoices/download/${url}`),

    connect,
    refresh: undefined,
    headers: api.defaults.headers.common,
  }

  apiEndpoints.refresh = refresh(apiEndpoints.refreshToken)

  api.interceptors.response.use(
    (response) => response,
    (err) => {
      const error = err.response
      if (
        error &&
        error.status === 401 &&
        error.config &&
        !err.config.__isRetryRequest &&
        error.config.url !== 'oauth/refresh-token'
      ) {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

        let jwtToken
        try {
          jwtToken = jwtDecode(refreshToken)
        } catch {
          handleLogoutWithHeaderReset()
        }

        if (jwtToken && jwtToken.exp > +new Date() / 1000) {
          error.config.__isRetryRequest = true
          return apiEndpoints
            .refreshToken(refreshToken)
            .then((response) => {
              if (response.status === 200) {
                localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken)
                setHeader('Authorization', `Bearer ${response.data.accessToken}`)
                return Promise.resolve(
                  api({
                    ...error.config,
                    headers: {
                      ...error.config.headers,
                      Authorization: `Bearer ${response.data.accessToken}`,
                    },
                  }),
                )
              }
              handleLogoutWithHeaderReset()
            })
            .catch(() => {
              if (notServer) {
                handleLogoutWithHeaderReset()
              }
            })
        }
        if (notServer) {
          handleLogoutWithHeaderReset()
        }
        error.config.__isRetryRequest = false
      }
      if (error && error.status === 503 && !err.config.__isRetryRequest && error.data.message === 'Not found') {
        // api.dispatch(UserActions.loginFailure())
      } else if (error && error.status === 503 && !err.config.__isRetryRequest) {
        handleLogoutWithHeaderReset()
      }
      return Promise.reject(error)
    },
  )

  return apiEndpoints
}

export const rawEndpoints = endpoints
export type ApiEndpoints = ReturnType<typeof endpoints>

export default endpoints(axiosApi)
